import { Component, Input, OnChanges, OnInit, SimpleChanges, ChangeDetectionStrategy } from '@angular/core'
import { select, Store } from '@ngrx/store'
import { Destroyable, toMoment, untilDestroy } from '@tokeet-frontend/tv3-platform'
import {
  AiCalendarDateAvailability,
  AiOffCalendarItemView,
  getDisabledHoursForDate,
  isDateAIDisabled,
  getAiOffCalendarItemDateRange,
} from '@tv3/containers/ai-responder/store/ai-settings.model'
import { selectAISettingsDisabledHoursView } from '@tv3/containers/ai-responder/store/ai-settings.selectors'
import moment from 'moment'

@Component({
  selector: 'app-ai-off-calendar-cell',
  templateUrl: './ai-off-calendar-cell.component.html',
  styleUrls: ['./ai-off-calendar-cell.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class AiOffCalendarCellComponent extends Destroyable implements OnInit, OnChanges {
  @Input() day: number

  @Input() today: boolean
  @Input() startOfMonth: boolean
  @Input() beforeToday: boolean

  availabilityTypes = AiCalendarDateAvailability
  availability: AiCalendarDateAvailability
  items: AiOffCalendarItemView[]

  constructor(private store: Store<any>) {
    super()
  }

  ngOnInit(): void {
    this.store
      .pipe(select(selectAISettingsDisabledHoursView), untilDestroy(this))
      .subscribe((items: AiOffCalendarItemView[]) => {
        this.availability = isDateAIDisabled(this.day, items)
        this.items = getDisabledHoursForDate(this.day, items)
      })
  }

  ngOnChanges(changes: SimpleChanges): void {}

  getTimeSlotTitle(items: AiOffCalendarItemView[]): string {
    if (!items || items.length === 0) return ''

    const slots = items.map((item) => getAiOffCalendarItemDateRange(item, this.day))

    // Sort by start time (by time only)
    slots.sort((a, b) => {
      const aMinutes = a.start.hours() * 60 + a.start.minutes()
      const bMinutes = b.start.hours() * 60 + b.start.minutes()
      return aMinutes - bMinutes
    })

    // Merge overlapping slots (by time only)
    const merged: { start: moment.Moment; end: moment.Moment }[] = []
    for (const slot of slots) {
      if (merged.length === 0) {
        merged.push({ ...slot })
      } else {
        const last = merged[merged.length - 1]
        const lastEndMinutes = last.end.hours() * 60 + last.end.minutes()
        const slotStartMinutes = slot.start.hours() * 60 + slot.start.minutes()
        if (slotStartMinutes <= lastEndMinutes) {
          if (slot.end.hours() * 60 + slot.end.minutes() > lastEndMinutes) {
            last.end = slot.end
          }
        } else {
          merged.push({ ...slot })
        }
      }
    }

    return merged
      .map(({ start, end }) => {
        const startHour = start.format('h')
        const endHour = end.format('h')
        const startMin = start.minutes()
        const endMin = end.minutes()
        const startPeriod = start.format('A')
        const endPeriod = end.format('A')
        // Both on the hour, same period
        if (startMin === 0 && endMin === 0 && startPeriod === endPeriod) {
          return `${startHour} - ${endHour} ${startPeriod}`
        }
        // Both same period, but at least one has minutes
        if (startPeriod === endPeriod) {
          const startStr = startMin === 0 ? startHour : start.format('h:mm')
          const endStr = endMin === 0 ? endHour : end.format('h:mm')
          return `${startStr} - ${endStr} ${startPeriod}`
        }
        // Different periods
        const startStr = startMin === 0 ? `${startHour} ${startPeriod}` : start.format('h:mm A')
        const endStr = endMin === 0 ? `${endHour} ${endPeriod}` : end.format('h:mm A')
        return `${startStr} - ${endStr}`
      })
      .join(', ')
  }
}
